import { useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import OfferCardFacilitiesFallbackContext from 'contexts/OfferList/OfferCardFacilitiesFallbackContext'
import { FormattedMessage, useIntl } from 'react-intl'
import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import OfferCardCarouselSection from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import type { OfferListEventHandler } from 'components/OfferList/OfferListEventsContext'
import { ChertoExperiments } from 'constants/experiments'
import { INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useLocalizedPlaces } from 'hooks/usePlace'
import { intlDateRangeFormatter } from 'lib/datetime/dateUtils'
import { trackClientEvent } from 'analytics/analytics'
import HomepageOfferCarousel from 'home/components/OfferCarousels/dedup/HomepageOfferCarousel'
import { useHomepageHeroOfferPublication } from 'home/components/OfferCarousels/dedup/HomepageOfferDedupProvider'
import { MAX_CAROUSEL_ITEM_COUNT } from 'home/components/OfferCarousels/common/constants'
import useRecentSearchHotelCarousel from 'home/hooks/useRecentSearchHotelCarousel'
import {
  buildRecentSearchHotelOfferUrlQuery,
  buildRecentSearchHotelViewAllUrl,
  excludeRecentSearchHotelOffers,
  RECENT_SEARCH_HOTEL_MIN_OFFERS,
} from 'home/recentSearchHotelCarousel/recentSearchHotelCarouselModel'
import {
  buildRecentSearchHotelCarouselAnalyticsData,
  buildRecentSearchHotelCarouselFilterContexts,
  RECENT_SEARCH_HOTEL_CAROUSEL_MODULE_ID,
} from './recentSearchHotelCarouselAnalytics'

interface Props {
  onListEvent: OfferListEventHandler
  excludedOfferIds?: Array<string>
}

const EMPTY_PLACE_IDS: Array<string> = []

function RecentSearchHotelOfferCarousel(props: Props) {
  const { onListEvent, excludedOfferIds } = props
  const intl = useIntl()
  const { publication, getCurrentPublication } = useHomepageHeroOfferPublication()
  const heroOfferIds = publication.offerIds
  const allExcludedOfferIds = useMemo(() => [
    ...(excludedOfferIds ?? EMPTY_PLACE_IDS),
    ...heroOfferIds,
  ], [excludedOfferIds, heroOfferIds])
  const experiment = ChertoExperiments.homePageRecentSearchesCarousel
  const { isTurnedOn, isAnyVariant, variation, triggerActivation } = useExperiment(experiment, {
    canActivate: false,
  })
  const { decision, filters, selectedDatesAndRoomsFilter } = useRecentSearchHotelCarousel(
    allExcludedOfferIds,
    isTurnedOn,
  )
  const trackedEligibilityKey = useRef<string | undefined>(undefined)
  const [firstEligibleAt, setFirstEligibleAt] = useState<string | undefined>(undefined)
  const placeIds = decision.context?.placeIds ?? EMPTY_PLACE_IDS
  const localizedPlaces = useLocalizedPlaces(placeIds)

  const destinationLabel = useMemo(() => {
    const context = decision.context
    if (!context) return undefined
    if (context.placeIds.length === 1) {
      return localizedPlaces[context.placeIds[0]]?.place?.name ?? context.query
    }
    return context.query
  }, [decision.context, localizedPlaces])

  const analyticsData = useMemo(
    () => buildRecentSearchHotelCarouselAnalyticsData(decision, {
      variant: variation,
      firstEligibleAt,
    }),
    [decision, firstEligibleAt, variation],
  )

  useEffect(() => {
    // The reporter may publish a new source during this commit. Never track a
    // decision calculated against a previous source or a loading placeholder.
    if (!publication.ready || publication !== getCurrentPublication()
      || !isTurnedOn || decision.status === 'loading') return
    let eligibleAt = firstEligibleAt
    if (decision.status === 'eligible') {
      if (!eligibleAt) {
        eligibleAt = new Date().toISOString()
        setFirstEligibleAt(eligibleAt)
      }
      triggerActivation()
    }
    const correlationKey = decision.context?.correlationKey ?? decision.reason
    const key = `${correlationKey}:${decision.status}:${decision.reason}:${variation ?? ''}`
    if (trackedEligibilityKey.current === key) return
    trackedEligibilityKey.current = key
    const eventAnalytics = buildRecentSearchHotelCarouselAnalyticsData(decision, {
      variant: variation,
      firstEligibleAt: eligibleAt,
    })
    trackClientEvent({
      subject: correlationKey,
      action: decision.reason,
      category: RECENT_SEARCH_HOTEL_CAROUSEL_MODULE_ID,
      type: 'operational',
      context: eventAnalytics
        ? buildRecentSearchHotelCarouselFilterContexts(eventAnalytics).map(filter => comluxgroup.createFilter_1_0_0(filter))
        : undefined,
    })
  }, [decision, firstEligibleAt, getCurrentPublication, isTurnedOn, publication, triggerActivation, variation])

  const title = useMemo(() => {
    const context = decision.context
    if (!destinationLabel || !context) return undefined
    if (context.dateMode === 'specific' && context.checkIn && context.checkOut) {
      const dates = intlDateRangeFormatter(
        context.checkIn,
        context.checkOut,
        INTL_DAY_MONTH_NAME_SHORT,
        intl.locale,
      )
      return (
        <FormattedMessage
          id="eIHKnVkcWGVfNrt1l45Z"
          defaultMessage="Hotels in {destination}, {dates}"
          description="Homepage heading showing hotels in a recently searched destination followed by the original stay dates"
          values={{ destination: destinationLabel, dates }}
        />
      )
    }
    return (
      <FormattedMessage
        id="ILZyTdY5VI9cdf5K-OQl"
        defaultMessage="Hotels in {destination}"
        description="Homepage heading showing hotels in a recently searched destination when the search has no stay dates"
        values={{ destination: destinationLabel }}
      />
    )
  }, [decision.context, destinationLabel, intl.locale])

  const viewAllUrl = useMemo(() => {
    if (!decision.context || !destinationLabel || decision.status !== 'eligible') return undefined
    return buildRecentSearchHotelViewAllUrl(decision.context, destinationLabel)
  }, [decision.context, decision.status, destinationLabel])

  const offerUrlQuery = useMemo(() => {
    if (!decision.context || !destinationLabel || decision.status !== 'eligible') return undefined
    return buildRecentSearchHotelOfferUrlQuery(decision.context, destinationLabel)
  }, [decision.context, decision.status, destinationLabel])

  const visibleOfferIds = useMemo(() => {
    return excludeRecentSearchHotelOffers(decision.verifiedOfferIds, allExcludedOfferIds).slice(0, MAX_CAROUSEL_ITEM_COUNT)
  }, [decision.verifiedOfferIds, allExcludedOfferIds])

  const offerList = useMemo(() => {
    if (!filters || visibleOfferIds.length < RECENT_SEARCH_HOTEL_MIN_OFFERS) return undefined
    return {
      offerIds: visibleOfferIds,
      fetching: false,
      key: filters.destinationIds?.join(',') ?? RECENT_SEARCH_HOTEL_CAROUSEL_MODULE_ID,
    }
  }, [filters, visibleOfferIds])

  if (
    !isAnyVariant
    || !publication.ready
    || decision.status !== 'eligible'
    || !filters
    || !title
    || !viewAllUrl
    || !offerUrlQuery
    || !offerList
  ) {
    return null
  }

  return (
    <RecentSearchHotelOfferCarouselBody
      analyticsData={analyticsData}
      filters={filters}
      offerList={offerList}
      offerUrlQuery={offerUrlQuery}
      onListEvent={onListEvent}
      selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
      title={title}
      viewAllUrl={viewAllUrl}
    />
  )
}

interface BodyProps {
  analyticsData: ReturnType<typeof buildRecentSearchHotelCarouselAnalyticsData>
  filters: App.OfferListFilters
  offerList: App.OfferList
  offerUrlQuery: string
  onListEvent: OfferListEventHandler
  selectedDatesAndRoomsFilter?: App.OfferListFilters
  title: React.ReactElement
  viewAllUrl: string
}

function RecentSearchHotelOfferCarouselBody(props: BodyProps) {
  const {
    analyticsData,
    filters,
    offerList,
    offerUrlQuery,
    onListEvent,
    selectedDatesAndRoomsFilter,
    title,
    viewAllUrl,
  } = props

  return (
    <HomepageOfferCarousel id="recentSearchHotelCarousel" mode="respect">
      <OfferCardFacilitiesFallbackContext.Provider value>
        <OfferListEventsProvider onListEvent={onListEvent} additionalData={analyticsData}>
          <OfferCardCarouselSection
            filters={filters}
            overrideOfferList={offerList}
            additionalOfferUrlQuery={offerUrlQuery}
            selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
            title={title}
            allLink={viewAllUrl}
            minimumOffersCount={RECENT_SEARCH_HOTEL_MIN_OFFERS}
            tileStyle="card-default"
            tileSize="medium"
            data-testid={RECENT_SEARCH_HOTEL_CAROUSEL_MODULE_ID}
          />
        </OfferListEventsProvider>
      </OfferCardFacilitiesFallbackContext.Provider>
    </HomepageOfferCarousel>
  )
}

export default React.memo(RecentSearchHotelOfferCarousel)
